import { ElevenLabsVoice, ElevenLabsStatus, GenerateNarrationRequest, GenerateNarrationResponse, FavoriteVoicesResponse, VoicesWithFavoritesResponse } from '../types';
import { API_BASE_URL } from '../../../config/api';
import { handleAPIError } from '../../../services/apiErrorHandler';

const API_BASE_URL_MUSIC = `${API_BASE_URL}/music-generation`;

export class ElevenLabsService {
  private static getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
    };
  }

  /**
   * Fetch all available ElevenLabs voices with favorites
   */
  static async fetchVoices(): Promise<ElevenLabsVoice[]> {
    try {
      const response = await fetch(`${API_BASE_URL_MUSIC}/voices/elevenlabs`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        await handleAPIError(response);
      }

      const data: VoicesWithFavoritesResponse = await response.json();
      
      if (data.success && data.data?.voices) {
        return data.data.voices;
      } else {
        throw new Error(data.message || 'Failed to fetch voices');
      }
    } catch (error) {
      console.error('Error fetching ElevenLabs voices:', error);
      throw error;
    }
  }

  /**
   * Get ElevenLabs service status
   */
  static async getStatus(): Promise<ElevenLabsStatus> {
    try {
      const response = await fetch(`${API_BASE_URL_MUSIC}/elevenlabs/status`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        await handleAPIError(response);
      }

      const data = await response.json();
      
      if (data.success && data.data) {
        return data.data;
      } else {
        throw new Error(data.message || 'Failed to get service status');
      }
    } catch (error) {
      console.error('Error getting ElevenLabs status:', error);
      throw error;
    }
  }

  /**
   * Generate narration using ElevenLabs
   */
  static async generateNarration(request: GenerateNarrationRequest): Promise<GenerateNarrationResponse> {
    try {
      const response = await fetch(`${API_BASE_URL_MUSIC}/narration`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        await handleAPIError(response);
      }

      const data = await response.json();
      
      if (data.success) {
        return data;
      } else {
        throw new Error(data.message || 'Failed to generate narration');
      }
    } catch (error) {
      console.error('Error generating narration:', error);
      throw error;
    }
  }

  /**
   * Get voice by ID from cached voices
   */
  static getVoiceById(voices: ElevenLabsVoice[], voiceId: string): ElevenLabsVoice | undefined {
    return voices.find(voice => voice.id === voiceId);
  }

  /**
   * Get recommended voice for content type
   */
  static getRecommendedVoice(voices: ElevenLabsVoice[], contentType: 'sermon' | 'devotional' | 'children' | 'general' = 'general'): ElevenLabsVoice | undefined {
    const filteredVoices = voices.filter(voice => {
      const gender = voice.labels?.gender?.toLowerCase();
      const age = voice.labels?.age?.toLowerCase();
      
      switch (contentType) {
        case 'sermon':
          return gender === 'male' && voice.category === 'premade';
        case 'devotional':
          return gender === 'female' && voice.category === 'premade';
        case 'children':
          return age === 'young' || voice.description?.toLowerCase().includes('young');
        default:
          return voice.category === 'premade';
      }
    });

    return filteredVoices.length > 0 ? filteredVoices[0] : voices[0];
  }

  /**
   * Group voices by category
   */
  static groupVoicesByCategory(voices: ElevenLabsVoice[]): Record<string, ElevenLabsVoice[]> {
    return voices.reduce((groups, voice) => {
      const category = voice.category || 'general';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(voice);
      return groups;
    }, {} as Record<string, ElevenLabsVoice[]>);
  }

  /**
   * Get voice display name with metadata
   */
  static getVoiceDisplayName(voice: ElevenLabsVoice): string {
    const gender = voice.labels?.gender;
    const age = voice.labels?.age;
    const accent = voice.labels?.accent;
    
    let displayName = voice.name;
    const metadata = [];
    
    if (gender) metadata.push(gender);
    if (age && age !== 'adult') metadata.push(age);
    if (accent && accent !== 'american') metadata.push(accent);
    
    if (metadata.length > 0) {
      displayName += ` (${metadata.join(', ')})`;
    }
    
    return displayName;
  }

  /**
   * Add voice to favorites
   */
  static async addFavoriteVoice(voiceId: string): Promise<FavoriteVoicesResponse> {
    try {
      const response = await fetch(`${API_BASE_URL_MUSIC}/voices/favorites`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ voiceId }),
      });

      if (!response.ok) {
        await handleAPIError(response);
      }

      const data = await response.json();
      
      if (data.success) {
        return data;
      } else {
        throw new Error(data.message || 'Failed to add favorite voice');
      }
    } catch (error) {
      console.error('Error adding favorite voice:', error);
      throw error;
    }
  }

  /**
   * Remove voice from favorites
   */
  static async removeFavoriteVoice(voiceId: string): Promise<FavoriteVoicesResponse> {
    try {
      const response = await fetch(`${API_BASE_URL_MUSIC}/voices/favorites/${voiceId}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        await handleAPIError(response);
      }

      const data = await response.json();
      
      if (data.success) {
        return data;
      } else {
        throw new Error(data.message || 'Failed to remove favorite voice');
      }
    } catch (error) {
      console.error('Error removing favorite voice:', error);
      throw error;
    }
  }

  /**
   * Get user's favorite voices
   */
  static async getFavoriteVoices(): Promise<string[]> {
    try {
      const response = await fetch(`${API_BASE_URL_MUSIC}/voices/favorites`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        await handleAPIError(response);
      }

      const data: FavoriteVoicesResponse = await response.json();
      
      if (data.success && data.data?.favoriteVoices) {
        return data.data.favoriteVoices;
      } else {
        throw new Error(data.message || 'Failed to get favorite voices');
      }
    } catch (error) {
      console.error('Error getting favorite voices:', error);
      throw error;
    }
  }
}
